/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { getProductDetails, readProductField, PRODUCT_DETAIL_TITLE_FIELD } from '@/lib/product/product-record-fields';

interface ProductAccordionProps {
    product: ShopperProducts.schemas['Product'];
}

export default function ProductAccordion({ product }: ProductAccordionProps): ReactElement | null {
    // Accordion content comes from the product record's `productDetail1…5` fields, rendered as a
    // bullet list. Empty fields are skipped, and the accordion renders nothing when none are
    // populated. The heading comes from `productDetailTitle`, defaulting to "Product Details".
    const detailValues = getProductDetails(product);

    if (detailValues.length === 0) {
        return null;
    }

    const title = readProductField(product, PRODUCT_DETAIL_TITLE_FIELD) ?? 'Product Details';

    return (
        <div className="w-full">
            {/* Open on page load (UCR-2773): the first (here, only) row is expanded by default. */}
            <Accordion type="multiple" defaultValue={['details']} className="w-full">
                {/* `!border-b` overrides shadcn's `last:border-b-0` so the single item keeps its keyline. */}
                <AccordionItem value="details" className="!border-b border-bm-white">
                    <AccordionTrigger className="py-7 text-left font-condensed text-xl font-bold leading-6 [&>svg]:size-6">
                        {title}
                    </AccordionTrigger>
                    <AccordionContent>
                        <ul className="list-disc space-y-1 pl-6 pb-2 text-base leading-[1.4] text-card-foreground/90">
                            {detailValues.map((value) => (
                                <li key={value}>{value}</li>
                            ))}
                        </ul>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
}
